/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { importJWK, SignJWT } from "jose";
import type { JWTHeaderParameters } from "jose";

import type {
  CryptoPrivateKey,
  CryptoPublicKey,
  JwtClaims,
  TWebCryptoKeyPair
} from "./TCryptoProvider";
import { CryptoProviderFactory } from "./CryptoProviderFactory";

export class WebCryptoProvider {
  private static readonly TAG: string = "WebCryptoProvider";

  private constructor() {}

  // ---------------------------------------------------------------------------
  // Key Management
  // ---------------------------------------------------------------------------

  /** Generates a 2048-bit RSA key pair in JWK format. */
  public static async generateRsaKeyPair(): Promise<TWebCryptoKeyPair> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: CryptoProviderFactory.RSA_ALGORITHM,
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: CryptoProviderFactory.SHA256_ALGORITHM
      },
      true,
      ["sign", "verify"]
    );

    const publicKey = await crypto.subtle.exportKey("jwk", keyPair.publicKey);
    const privateKey = await crypto.subtle.exportKey("jwk", keyPair.privateKey);

    return {
      publicKey,
      privateKey
    };
  }

  /** Returns a PEM public key from JWK input. */
  public static async getPublicKeyPem(
    publicKey: CryptoPublicKey
  ): Promise<string> {
    const spkiBuffer = await WebCryptoProvider.getPublicKeySpki(publicKey);
    const derBase64 = CryptoProviderFactory.arrayBufferToBase64(spkiBuffer);

    return (
      "-----BEGIN PUBLIC KEY-----\n" +
      derBase64 +
      "\n-----END PUBLIC KEY-----\n"
    );
  }

  /** Exports a JWK public key as SPKI bytes. */
  public static async getPublicKeySpki(
    publicKeyJwk: CryptoPublicKey
  ): Promise<ArrayBuffer> {
    if (typeof publicKeyJwk === "string") {
      throw new Error("Expected JWK public key");
    }

    const publicKey = await crypto.subtle.importKey(
      "jwk",
      publicKeyJwk,
      {
        name: CryptoProviderFactory.RSA_ALGORITHM,
        hash: CryptoProviderFactory.SHA256_ALGORITHM
      },
      true,
      ["verify"]
    );

    return await crypto.subtle.exportKey("spki", publicKey);
  }

  // ---------------------------------------------------------------------------
  // Hashing & Utility
  // ---------------------------------------------------------------------------

  /** Computes SHA-256 hash as a hex string. */
  public static async computeSha256(data: string): Promise<string> {
    const encoded = new TextEncoder().encode(data);

    const hashBuffer = await crypto.subtle.digest(
      CryptoProviderFactory.SHA256_ALGORITHM,
      encoded
    );

    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  /** Generates an RFC4122 v4 UUID. */
  public static generateUuidV4(): string {
    const bytes = new Uint8Array(16);

    crypto.getRandomValues(bytes);

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return (
      hex.slice(0, 8) +
      "-" +
      hex.slice(8, 12) +
      "-" +
      hex.slice(12, 16) +
      "-" +
      hex.slice(16, 20) +
      "-" +
      hex.slice(20)
    );
  }

  // ---------------------------------------------------------------------------
  // Signing
  // ---------------------------------------------------------------------------

  /** Signs DLID with RSA-SHA256 and returns base64url signature. */
  public static async signDlid(
    dlid: string,
    privateKeyJwk: CryptoPrivateKey
  ): Promise<string> {
    if (typeof privateKeyJwk === "string") {
      throw new Error("Expected JWK private key");
    }

    const privateKey = await crypto.subtle.importKey(
      "jwk",
      privateKeyJwk,
      {
        name: CryptoProviderFactory.RSA_ALGORITHM,
        hash: CryptoProviderFactory.SHA256_ALGORITHM
      },
      false,
      ["sign"]
    );

    const signature = await crypto.subtle.sign(
      CryptoProviderFactory.RSA_ALGORITHM,
      privateKey,
      new TextEncoder().encode(dlid)
    );

    return WebCryptoProvider.arrayBufferToBase64Url(signature);
  }

  /** Signs JWT claims with RS256 using JWK private key. */
  public static async signJwtRS256(
    claims: JwtClaims,
    privateKey: CryptoPrivateKey,
    header: JWTHeaderParameters
  ): Promise<string> {
    if (typeof privateKey === "string") {
      throw new Error("WebCryptoProvider expected JsonWebKey private key");
    }

    const cryptoKey = await importJWK(
      {
        ...privateKey,
        alg: CryptoProviderFactory.RS256_ALGORITHM
      },
      CryptoProviderFactory.RS256_ALGORITHM
    );

    return await new SignJWT(claims).setProtectedHeader(header).sign(cryptoKey);
  }

  /** Signs JWT claims with RS512 using JWK private key. */
  public static async signJwtRS512(
    claims: JwtClaims,
    privateKey: CryptoPrivateKey,
    header: JWTHeaderParameters
  ): Promise<string> {
    if (typeof privateKey === "string") {
      throw new Error("WebCryptoProvider expected JsonWebKey private key");
    }

    const cryptoKey = await importJWK(
      {
        ...privateKey,
        alg: CryptoProviderFactory.RS512_ALGORITHM
      },
      CryptoProviderFactory.RS512_ALGORITHM
    );

    return await new SignJWT(claims).setProtectedHeader(header).sign(cryptoKey);
  }

  // ---------------------------------------------------------------------------
  // Private Helpers
  // ---------------------------------------------------------------------------

  /** Converts ArrayBuffer bytes to base64url. */
  private static arrayBufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";

    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    let result = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_");

    while (result.endsWith("=")) {
      result = result.slice(0, -1);
    }

    return result;
  }
}
